//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { SmartInputUIRefreshKey } from '@universal-scheduling/contexts/scheduler/schedulerSharedStateModels';

/**
 * The SmartInput is not a controlled input because draft-js was not built to work
 * in that fashion. In order to ensure that the Smart Input is properly rendered with updated values
 * from a source outside the Smart Input (such as an update from another piece of react state),
 * we will generate a unique key to force it to update display the new value.
 * The forced refresh is being accomplished with the React `key` property.
 *
 * This key should be provided to a parent component of the Smart Input.
 *
 * Example:
 * Other Component separate from the Smart Input
 * <button onClick={()=>{
 *    setSharedState((prev)=>{
 *       return {
 *        ...prev,
 *        mySharedCaption: newCaption,
 *        smartInputUIRefreshKey: getSmartInputUIRefreshKey('caption') // this forces React to rerender the smart input meaning that it will display the new value
 *    },
 *    }))
 *
 * }
 * >Button</button>
 *
 * Parent Component of Smart Input
 * <div key={smartInputUIRefreshKey}>
 *     <SmartInput  value={mySharedCaption} /> // this value will not appear in the smart caption until the UI is forced to be refreshed with the key
 * </div>
 *
 * This function should only be called when an update has occurred that the Smart Input cannot read and we want to force it
 * to display that updated value
 *
 * We are using date to have an auto-incrementing large number to make our key unique.
 * The template string allows us to guarantee that we don't have collisions with other keys improving performance
 * DO NOT PUT THE KEY ON THE SMART INPUT ITSELF
 * DO NOT CALL THIS FUNCTION ON EVERY RENDER, ONLY CALL IT WHEN WE WANT TO FORCE THE UPDATE TO DISPLAY
 */
export const getSmartInputUIRefreshKey = (
  name: string = 'default',
): SmartInputUIRefreshKey => {
  return `${Date.now()}--${name}-smart-input-refresh-key`;
};
